import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle2,
  Mail,
  Lock,
  Store,
  LogOut,
  LayoutGrid,
  AlertCircle,
  Eye,
  EyeOff,
  Loader2,
  Users,
  UploadCloud,
  Plus,
  Trash2,
  X,
  Image as ImageIcon,
  ShoppingCart,
  Globe,
  Settings,
  Percent,
  Shield,
  MapPin,
  Info,
  PartyPopper,
  Trophy,
  FileDown,
} from "lucide-react";

import MediaLibraryComponent from "../media/MediaLibraryComponent";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { ReactSelect } from "@/components/ui/react-select";
import { Country, State } from "country-state-city";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import apiClient from "../../api/apiClient";
import { setuser_data, setcurrent_store } from "../../core/redux/action";
import { all_routes } from "../../Router/all_routes";
import { currencySettingsData } from "../../core/json/currencySettings";
import { languageSettingsData } from "../../core/json/languageSettings";
import timezones from "timezones.json";

export default function Onboarding() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const token = useSelector((state) => state.bearer_token);
  const rawUserData = useSelector((state) => state.user_data);
  const userData = useMemo(() => rawUserData || {}, [rawUserData]);

  const [activeStep, setActiveStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState({
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
  });

  const [onboardingErrors, setOnboardingErrors] = useState({
    email: "",
    password: "",
    store: "",
  });

  const [email, setEmail] = useState("");
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [skippedPassword, setSkippedPassword] = useState(false);
  const [storeData, setStoreData] = useState({
    name: "",
    alternate_name: "",
    logo: "",
    description: "",
    email: "",
    phone: "",
    mobile: "",
    fax: "",
    website: "",
    address: "",
    city: "",
    postcode: "",
    state: "",
    country: "",
    zip: "",
    time_zone: "",
    time_format: "",
    language: "",
    default_currency: "",
    price_format: "1,234.56",
    decimal_format: "2",
    default_tax_class: "NA",
    tax_percentage: 0,
    registration_no: "",
    start_time: "",
    end_time: "",
    default_address: "",
    api_key: "",
    accounting_method: "cash",
    send_email_from: "",
    is_send_email_from_verified: false,
    receive_all_emails: false,
    require_two_factor_auth: false,
    charge_restocking_fee: false,
    restocking_fee_percentage: 0,
    charge_deposit: false,
    deposit_amount: 0,
    charge_tax_on_deposit: false,
    deposit_tax_class: "",
    default_charge_deposit: false,
    lock_screen_enabled: false,
    screen_timeout_minutes: 1,
    terms_and_conditions: "",
    status: "active",
  });

  const [showCustomerImportModal, setShowCustomerImportModal] = useState(false);
  const [customerImportFile, setCustomerImportFile] = useState(null);
  const [showProductImportModal, setShowProductImportModal] = useState(false);
  const [productImportFile, setProductImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState(null);

  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [selectedLogoId, setSelectedLogoId] = useState(null);
  const [selectedLogoDetails, setSelectedLogoDetails] = useState(null);

  const handleMediaSelect = (selectedId, selectedObject) => {
    setSelectedLogoId(selectedId);
    setSelectedLogoDetails(selectedObject || null);
    if (selectedObject?.url) {
      setStoreData((prev) => ({ ...prev, logo: selectedObject.url }));
    }
  };

  // Data fetching
  const { data: onboardingStatus } = useQuery({
    queryKey: ["onboarding-status"],
    queryFn: async () => {
      const res = await apiClient.get("/init-page/onboarding");
      return res.data.data;
    },
    enabled: !!token,
  });

  useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get("/store");
      return res.data.data || [];
    },
    enabled: !!token,
  });

  // Effects to check initial state
  useEffect(() => {
    if (userData && onboardingStatus) {
      const isEmailPending = !onboardingStatus.is_email_changed;
      const isPasswordPending = !onboardingStatus.is_password_updated;
      const isStorePending = !onboardingStatus.is_store_setup_completed;
      const isProductsPending = !onboardingStatus.have_products;
      const isCustomersPending = !onboardingStatus.have_customers;

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCompletedSteps((prev) => ({
        ...prev,
        1: !isEmailPending,
        2: !isPasswordPending,
        3: !isStorePending,
        4: !isProductsPending,
        5: !isCustomersPending,
      }));

      // Set initial active step based on what's pending
      // skippedPassword prevents the useEffect from pulling the user back to step 2
      // after a failed or background API call for "continue-current-password"
      if (isEmailPending) setActiveStep(1);
      else if (isPasswordPending && !skippedPassword) setActiveStep(2);
      else if (isStorePending) setActiveStep(3);
      else if (isProductsPending) setActiveStep(4);
      else if (isCustomersPending) setActiveStep(5);
      else {
        setActiveStep(6); // Show the "All Done" screen if all steps are met
      }
    }
  }, [userData, onboardingStatus, skippedPassword]);

  // Mutations
  const emailMutation = useMutation({
    mutationFn: (email) =>
      apiClient.put(`/employee/${userData?.employee?.id}`, {
        ...userData.employee,
        email,
      }),
    onSuccess: (res) => {
      dispatch(setuser_data({ ...userData, employee: res.data.data }));
      queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
      toast.success("Email updated successfully!");
      setActiveStep(2);
    },
    onError: (err) =>
      setOnboardingErrors((prev) => ({
        ...prev,
        email: err.response?.data?.message || "Failed to update email",
      })),
  });

  const passwordMutation = useMutation({
    mutationFn: async (data) => {
      await apiClient.patch(`/user/${userData?.id}/change-password`, {
        current_password: data.current,
        password: data.new,
        password_confirmation: data.confirm,
      });
      const meRes = await apiClient.get("/me");
      return meRes.data.data;
    },
    onSuccess: (updatedUserData) => {
      dispatch(setuser_data(updatedUserData));
      queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
      toast.success("Password changed successfully!");
      setActiveStep(3);
    },
    onError: (err) =>
      setOnboardingErrors((prev) => ({
        ...prev,
        password: err.response?.data?.message || "Failed to change password",
      })),
  });

  const continuePasswordMutation = useMutation({
    mutationFn: async () => {
      await apiClient.put(`/user/${userData?.id}`, { is_temp_pass: false });
      const meRes = await apiClient.get("/me");
      return meRes.data.data;
    },
    onSuccess: (updatedUserData) => {
      dispatch(setuser_data(updatedUserData));
      queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
    },
    onError: () => {
      // Silently ignore - user has already been advanced to the next step
    },
  });

  const importProductMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post("/import/products", formData);
      return res;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
      if (res.status === 207) {
        setImportErrors(res.data);
      } else {
        toast.success("Products imported successfully");
      }
      setShowProductImportModal(false);
      setProductImportFile(null);
    },
    onError: (err) => {
      const errorData = err.response?.data;
      if (errorData && (errorData.error || errorData.message)) {
        setImportErrors({
          message: errorData.message || "Failed to import products",
          data: {
            failed: "All",
            failures: [
              {
                row: "N/A",
                attribute: "System Error",
                errors: [errorData.error || errorData.message],
              },
            ],
          },
        });
        setShowProductImportModal(false);
        setProductImportFile(null);
      } else {
        toast.error("Failed to import products");
      }
    },
  });

  const handleDownloadProductTemplate = async () => {
    try {
      toast.success("Downloading template...");
      const res = await apiClient.get("/import/products/template", {
        responseType: "blob",
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        params: { _t: Date.now() },
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "product_import_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  };

  const importCustomerMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post("/import/customers", formData);
      return res;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
      if (res.status === 207) {
        setImportErrors(res.data);
      } else {
        toast.success("Customers imported successfully");
        setActiveStep(6);
      }
      setShowCustomerImportModal(false);
      setCustomerImportFile(null);
    },
    onError: (err) => {
      const errorData = err.response?.data;
      if (errorData && (errorData.error || errorData.message)) {
        setImportErrors({
          message: errorData.message || "Failed to import customers",
          data: {
            failed: "All",
            failures: [
              {
                row: "N/A",
                attribute: "System Error",
                errors: [errorData.error || errorData.message],
              },
            ],
          },
        });
        setShowCustomerImportModal(false);
        setCustomerImportFile(null);
      } else {
        toast.error("Failed to import customers");
      }
    },
  });

  const handleDownloadCustomerTemplate = async () => {
    try {
      toast.success("Downloading template...");
      const res = await apiClient.get("/import/customers/template", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "customer_import_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  };

  const storeMutation = useMutation({
    mutationFn: (data) =>
      apiClient.post("/store", { ...data, status: "active" }),
    onSuccess: (res) => {
      dispatch(setcurrent_store(res.data.data));
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
      setCompletedSteps((prev) => ({ ...prev, 3: true }));
      setActiveStep(4);
      toast.success("Store created successfully!");
    },
    onError: (err) =>
      setOnboardingErrors((prev) => ({
        ...prev,
        store: err.response?.data?.message || "Failed to create store",
      })),
  });

  // Handlers
  const handleEmailSubmit = () => {
    if (!email) {
      setOnboardingErrors((prev) => ({ ...prev, email: "Email is required" }));
      return;
    }
    emailMutation.mutate(email);
  };

  const handlePasswordSubmit = () => {
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      setOnboardingErrors((prev) => ({
        ...prev,
        password: "All fields are required",
      }));
      return;
    }
    if (passwords.new !== passwords.confirm) {
      setOnboardingErrors((prev) => ({
        ...prev,
        password: "Passwords do not match",
      }));
      return;
    }
    passwordMutation.mutate(passwords);
  };

  const handleSkipPassword = () => {
    setSkippedPassword(true);
    setCompletedSteps((prev) => ({ ...prev, 2: true }));
    setActiveStep(3);
    continuePasswordMutation.mutate();
  };

  const handleStoreSubmit = (e) => {
    e.preventDefault();
    if (
      !storeData.default_currency ||
      storeData.default_currency === "" ||
      (typeof storeData.default_currency === "object" &&
        !storeData.default_currency?.id)
    ) {
      setOnboardingErrors((prev) => ({
        ...prev,
        store: "Default currency is required!",
      }));
      return;
    }
    storeMutation.mutate(storeData);
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = all_routes.signin;
  };

  const onFinish = () => {
    localStorage.setItem("onboardingOptionalDone", "true");
    navigate(all_routes.dashboard);
  };

  const onboardingCountries = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        value: c.isoCode,
        label: c.name,
      })),
    [],
  );

  const onboardingCountryCode = useMemo(() => {
    const c = Country.getAllCountries().find(
      (c) => c.name === storeData.country,
    );
    return c ? c.isoCode : "";
  }, [storeData.country]);

  const onboardingStateOptions = useMemo(
    () =>
      onboardingCountryCode
        ? State.getStatesOfCountry(onboardingCountryCode).map((s) => ({
          value: s.name,
          label: s.name,
        }))
        : [],
    [onboardingCountryCode],
  );

  const handleStoreChange = (name, value) => {
    let finalValue = value;
    if (
      [
        "tax_percentage",
        "restocking_fee_percentage",
        "deposit_amount",
        "screen_timeout_minutes",
      ].includes(name)
    ) {
      finalValue = value === "" ? 0 : parseFloat(value);
    }
    setStoreData((prev) => ({ ...prev, [name]: finalValue }));
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
      {/* Sidebar */}
      <div className="hidden md:flex w-[280px] min-[1440px]:w-[360px] min-[2160px]:w-[420px] border-r bg-card/40 flex-col p-6 lg:p-8 backdrop-blur-sm transition-all duration-300 overflow-y-auto custom-scrollbar">
        <div className="flex-1 flex flex-col">
          <div className="flex items-center gap-3 mb-8 lg:mb-12">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center text-primary-foreground shadow-lg shadow-primary/20 hover:scale-105 transition-transform duration-300">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Setup Wizard</h1>
              <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold">
                Easy Retail Flow
              </p>
            </div>
          </div>

          <div className="space-y-1.5 lg:space-y-2 mb-8">
            <StepItem
              number={1}
              title="Update Email"
              isActive={activeStep === 1}
              isCompleted={completedSteps[1]}
              disabled={completedSteps[1]}
              onClick={() => setActiveStep(1)}
            />
            <StepItem
              number={2}
              title="Secure Account"
              isActive={activeStep === 2}
              isCompleted={completedSteps[2]}
              disabled={completedSteps[2]}
              onClick={() => setActiveStep(2)}
            />
            <StepItem
              number={3}
              title="Setup Store"
              isActive={activeStep === 3}
              isCompleted={completedSteps[3]}
              disabled={completedSteps[3]}
              onClick={() => setActiveStep(3)}
            />
            <StepItem
              number={4}
              title="Add Products"
              isActive={activeStep === 4}
              isCompleted={completedSteps[4]}
              disabled={completedSteps[4]}
              isOptional
              onClick={() => setActiveStep(4)}
            />
            <StepItem
              number={5}
              title="Add Customers"
              isActive={activeStep === 5}
              isCompleted={completedSteps[5]}
              disabled={completedSteps[5]}
              isOptional
              onClick={() => setActiveStep(5)}
            />
          </div>

          <div className="mt-auto space-y-4">
            <Button
              variant="outline"
              className="w-full justify-start gap-2 h-11"
              onClick={() => navigate(all_routes.dashboard)}
            >
              <LayoutGrid className="h-4 w-4" />
              Dashboard
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 h-11 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You will need to sign back in to complete your account
                    setup.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleLogout}>
                    Sign Out
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative flex-1 min-h-0 overflow-y-auto bg-muted/5 p-4 md:p-8 lg:p-10 transition-all duration-300">
        <div className="md:hidden flex items-center justify-between mb-8 pb-4 border-b">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            <span className="font-bold">Setup Wizard</span>
          </div>
          <span className="text-xs font-medium bg-primary/10 text-primary px-2 py-1 rounded">
            {activeStep > 5 ? "Complete" : `Step ${activeStep} of 5`}
          </span>
        </div>
        <div className="max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto flex flex-col">
          <Tabs value={activeStep.toString()} className="w-full">
            <TabsContent value="1">
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <header className="space-y-1.5 pt-4">
                  <h2 className="text-3xl font-bold tracking-tight">
                    Update Email Address
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Please provide a valid email address for account security
                    and communications.
                  </p>
                </header>

                <div className="bg-destructive/10 text-destructive p-4 rounded-lg flex gap-3 items-start border border-destructive/20 shadow-sm transition-all hover:bg-destructive/[0.08]">
                  <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-lg">Action Required</p>
                    <p className="opacity-90 leading-relaxed text-sm">
                      Your current email belongs to a default domain. For your
                      security, this must be updated before you can continue.
                    </p>
                  </div>
                </div>

                <div className="space-y-5 pt-2">
                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="text-sm font-semibold text-muted-foreground ml-1"
                    >
                      New Email Address
                    </Label>
                    <div className="relative group">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                      <Input
                        id="email"
                        placeholder="john@example.com"
                        className="pl-10"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    {onboardingErrors.email && (
                      <p className="text-sm font-medium text-destructive flex items-center gap-2 mt-2 ml-1">
                        <AlertCircle className="h-4 w-4" />
                        {onboardingErrors.email}
                      </p>
                    )}
                  </div>

                  <Button
                    className="w-full h-12 text-lg font-bold shadow-lg shadow-primary/20 rounded-lg transition-all active:scale-[0.98]"
                    onClick={handleEmailSubmit}
                    disabled={emailMutation.isPending}
                  >
                    {emailMutation.isPending ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      "Update Email Address"
                    )}
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="2">
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <header className="space-y-1.5 pt-4">
                  <h2 className="text-3xl font-bold tracking-tight">
                    Secure Account
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Protect your business data by setting a strong, personal
                    password.
                  </p>
                </header>

                <div className="bg-amber-500/10 text-amber-600 dark:text-amber-400 p-4 rounded-lg flex gap-3 items-start border border-amber-500/20 shadow-sm transition-all hover:bg-amber-500/[0.08]">
                  <Lock className="h-5 w-5 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-lg">Password Security</p>
                    <p className="opacity-90 leading-relaxed text-sm">
                      You are currently using a temporary password. Update it
                      now to ensure only you can access your store data.
                    </p>
                  </div>
                </div>

                <div className="space-y-5 pt-2">
                  <PasswordField
                    id="current-pass"
                    label="Current Password"
                    value={passwords.current}
                    onChange={(e) =>
                      setPasswords({ ...passwords, current: e.target.value })
                    }
                    show={showPasswords.current}
                    toggleShow={() =>
                      setShowPasswords({
                        ...showPasswords,
                        current: !showPasswords.current,
                      })
                    }
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PasswordField
                      id="new-pass"
                      label="New Password"
                      value={passwords.new}
                      onChange={(e) =>
                        setPasswords({ ...passwords, new: e.target.value })
                      }
                      show={showPasswords.new}
                      toggleShow={() =>
                        setShowPasswords({
                          ...showPasswords,
                          new: !showPasswords.new,
                        })
                      }
                      hint="Min 8 characters"
                    />
                    <PasswordField
                      id="confirm-pass"
                      label="Confirm New Password"
                      value={passwords.confirm}
                      onChange={(e) =>
                        setPasswords({ ...passwords, confirm: e.target.value })
                      }
                      show={showPasswords.confirm}
                      toggleShow={() =>
                        setShowPasswords({
                          ...showPasswords,
                          confirm: !showPasswords.confirm,
                        })
                      }
                    />
                  </div>

                  {onboardingErrors.password && (
                    <p className="text-sm font-medium text-destructive flex items-center gap-2 mt-2 ml-1 animate-in fade-in slide-in-from-top-1">
                      <AlertCircle className="h-4 w-4" />
                      {onboardingErrors.password}
                    </p>
                  )}

                  <div className="flex flex-col gap-3 pt-4">
                    <Button
                      className="w-full h-12 text-lg font-bold shadow-lg shadow-primary/20 rounded-lg transition-all active:scale-[0.98]"
                      onClick={handlePasswordSubmit}
                      disabled={passwordMutation.isPending}
                    >
                      {passwordMutation.isPending ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        "Update Password"
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full h-11 text-muted-foreground hover:text-foreground font-medium"
                      onClick={handleSkipPassword}
                    >
                      Skip for now (Change in Settings later)
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="3">
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-6">
                <header className="space-y-1.5 pt-4">
                  <h2 className="text-3xl font-bold tracking-tight">
                    Create Your Store
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Tell us about your business to help us configure your
                    settings.
                  </p>
                </header>

                <form onSubmit={handleStoreSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <Info className="h-5 w-5 text-primary" />
                        Basic Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                          <Label>
                            Store Name{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <div className="relative group">
                            <Store className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              name="name"
                              placeholder="My Retail Shop"
                              className="pl-10"
                              required
                              value={storeData.name}
                              onChange={(e) =>
                                handleStoreChange("name", e.target.value)
                              }
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Alternate Name</Label>
                          <Input
                            name="alternate_name"
                            placeholder="Trading Name"
                            value={storeData.alternate_name}
                            onChange={(e) =>
                              handleStoreChange(
                                "alternate_name",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Registration Number</Label>
                          <Input
                            name="registration_no"
                            placeholder="BRN123456"
                            value={storeData.registration_no}
                            onChange={(e) =>
                              handleStoreChange(
                                "registration_no",
                                e.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Store Email</Label>
                          <Input
                            type="email"
                            name="email"
                            placeholder="contact@store.com"
                            value={storeData.email}
                            onChange={(e) =>
                              handleStoreChange("email", e.target.value)
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            name="phone"
                            placeholder="+1 234 567 890"
                            value={storeData.phone}
                            onChange={(e) =>
                              handleStoreChange("phone", e.target.value)
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Mobile</Label>
                          <Input
                            name="mobile"
                            placeholder="+1 987 654 321"
                            value={storeData.mobile}
                            onChange={(e) =>
                              handleStoreChange("mobile", e.target.value)
                            }
                          />
                        </div>

                        <div className="space-y-2 md:col-span-2">
                          <Label>Store Website</Label>
                          <Input
                            type="url"
                            name="website"
                            placeholder="https://yourstore.com"
                            value={storeData.website}
                            onChange={(e) =>
                              handleStoreChange("website", e.target.value)
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Store Logo</Label>
                          <div className="flex items-center gap-4">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setIsMediaOpen(true)}
                            >
                              <ImageIcon className="mr-2 h-4 w-4" />
                              {storeData.logo ? "Change Logo" : "Select Logo"}
                            </Button>
                            {(selectedLogoDetails || storeData.logo) && (
                              <div className="relative h-20 w-20 border rounded-lg overflow-hidden bg-muted shrink-0">
                                <img
                                  src={
                                    selectedLogoDetails?.url || storeData.logo
                                  }
                                  alt="Store Logo"
                                  className="h-full w-full object-contain"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedLogoId(null);
                                    setSelectedLogoDetails(null);
                                    handleStoreChange("logo", "");
                                  }}
                                  className="absolute top-0.5 right-0.5 h-5 w-5 bg-destructive rounded-full flex items-center justify-center text-white hover:bg-destructive/80 transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                          <div className="space-y-2">
                            <Label>Store Description</Label>
                            <Textarea
                              name="description"
                              placeholder="Briefly describe your store..."
                              className="min-h-[100px]"
                              value={storeData.description}
                              onChange={(e) =>
                                handleStoreChange("description", e.target.value)
                              }
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Terms & Conditions</Label>
                            <Textarea
                              name="terms_and_conditions"
                              placeholder="Your business terms..."
                              className="min-h-[100px]"
                              value={storeData.terms_and_conditions}
                              onChange={(e) =>
                                handleStoreChange(
                                  "terms_and_conditions",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Address Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-primary" />
                        Address Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="md:col-span-4 space-y-2">
                          <Label>Store Address</Label>
                          <Input
                            name="address"
                            placeholder="123 Business St, Suite 100"
                            value={storeData.address}
                            onChange={(e) =>
                              handleStoreChange("address", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>City</Label>
                          <Input
                            name="city"
                            placeholder="New York"
                            value={storeData.city}
                            onChange={(e) =>
                              handleStoreChange("city", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Country</Label>
                          <ReactSelect
                            options={onboardingCountries}
                            value={
                              onboardingCountries.find(
                                (c) => c.label === storeData.country,
                              ) || null
                            }
                            onChange={(opt) => {
                              handleStoreChange(
                                "country",
                                opt ? opt.label : "",
                              );
                              handleStoreChange("state", "");
                            }}
                            placeholder="Select country"
                            isClearable
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>State / Province</Label>
                          <ReactSelect
                            options={onboardingStateOptions}
                            value={
                              onboardingStateOptions.find(
                                (s) => s.value === storeData.state,
                              ) || null
                            }
                            onChange={(opt) =>
                              handleStoreChange("state", opt ? opt.value : "")
                            }
                            placeholder={
                              onboardingCountryCode
                                ? "Select state"
                                : "Select country first"
                            }
                            isDisabled={!onboardingCountryCode}
                            isClearable
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Postcode / Zip</Label>
                          <Input
                            name="postcode"
                            placeholder="Postcode"
                            value={storeData.postcode}
                            onChange={(e) =>
                              handleStoreChange("postcode", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Regional & Configuration */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <Globe className="h-5 w-5 text-primary" />
                        Regional Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Timezone *</Label>
                          <ReactSelect
                            options={timezones?.map((t) => ({
                              value: `${t.value}-${t.abbr}`,
                              label: t.text,
                            }))}
                            value={timezones
                              ?.map((t) => ({
                                value: `${t.value}-${t.abbr}`,
                                label: t.text,
                              }))
                              .find((opt) => opt.value === storeData.time_zone)}
                            onChange={(opt) =>
                              handleStoreChange("time_zone", opt?.value)
                            }
                            placeholder="Select Timezone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Language *</Label>
                          <ReactSelect
                            options={languageSettingsData?.map((l) => ({
                              value: l.code,
                              label: l.language,
                            }))}
                            value={languageSettingsData
                              ?.map((l) => ({
                                value: l.code,
                                label: l.language,
                              }))
                              .find((opt) => opt.value === storeData.language)}
                            onChange={(opt) =>
                              handleStoreChange("language", opt?.value)
                            }
                            placeholder="Select Language"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Default Currency *</Label>
                          <ReactSelect
                            options={currencySettingsData?.map((c) => ({
                              value: String(c.id),
                              label: `${c.name} (${c.symbol})`,
                              currency: c,
                            }))}
                            value={
                              currencySettingsData
                                ?.map((c) => ({
                                  value: String(c.id),
                                  label: `${c.name} (${c.symbol})`,
                                  currency: c,
                                }))
                                .find(
                                  (opt) =>
                                    opt.value ===
                                    String(
                                      typeof storeData.default_currency ===
                                        "object"
                                        ? storeData.default_currency?.id
                                        : storeData.default_currency,
                                    ),
                                ) || null
                            }
                            onChange={(opt) =>
                              handleStoreChange(
                                "default_currency",
                                opt?.currency || "",
                              )
                            }
                            placeholder="Select Currency"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Time Format</Label>
                          <ReactSelect
                            options={[
                              { value: "12h", label: "12-hour (AM/PM)" },
                              { value: "24h", label: "24-hour" },
                            ]}
                            value={[
                              { value: "12h", label: "12-hour (AM/PM)" },
                              { value: "24h", label: "24-hour" },
                            ].find(
                              (opt) => opt.value === storeData.time_format,
                            )}
                            onChange={(opt) =>
                              handleStoreChange("time_format", opt?.value)
                            }
                            placeholder="Select Format"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Tax Settings */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <Percent className="h-5 w-5 text-primary" />
                        Tax & Sales Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Default Tax Class</Label>
                            <ReactSelect
                              options={[
                                { value: "GST", label: "GST" },
                                { value: "VAT", label: "VAT" },
                                { value: "NA", label: "None" },
                                { value: "Other", label: "Other" },
                              ]}
                              value={
                                [
                                  { value: "GST", label: "GST" },
                                  { value: "VAT", label: "VAT" },
                                  { value: "NA", label: "None" },
                                  { value: "Other", label: "Other" },
                                ].find(
                                  (opt) =>
                                    opt.value ===
                                    storeData.default_tax_class,
                                ) ||
                                (storeData.default_tax_class &&
                                  !["GST", "VAT", "NA"].includes(
                                    storeData.default_tax_class,
                                  )
                                  ? { value: "Other", label: "Other" }
                                  : null)
                              }
                              onChange={(opt) => {
                                setStoreData((prev) => ({
                                  ...prev,
                                  default_tax_class: opt?.value || "NA",
                                  tax_percentage:
                                    opt?.value === "NA"
                                      ? 0
                                      : prev.tax_percentage,
                                }));
                              }}
                              placeholder="Select Class"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Tax (%)</Label>
                            <Input
                              type="number"
                              name="tax_percentage"
                              placeholder="0.00"
                              value={storeData.tax_percentage}
                              onChange={(e) =>
                                handleStoreChange(
                                  "tax_percentage",
                                  e.target.value,
                                )
                              }
                              disabled={
                                storeData.default_tax_class === "NA"
                              }
                              min="0"
                              max="100"
                            />
                          </div>
                          {!["GST", "VAT", "NA"].includes(
                            storeData.default_tax_class,
                          ) && (
                              <div className="md:col-span-2 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                <Label>Custom Tax Name</Label>
                                <Input
                                  name="default_tax_class"
                                  placeholder="Enter tax name (e.g. Sales Tax)"
                                  value={
                                    storeData.default_tax_class === "Other"
                                      ? ""
                                      : storeData.default_tax_class
                                  }
                                  onChange={(e) =>
                                    handleStoreChange(
                                      "default_tax_class",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                            )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Refund & Deposit Settings - hidden, not functional yet */}
                  <Card style={{ display: 'none' }}>
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        Refund &amp; Deposit Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <Label>Charge Restocking Fee</Label>
                            <Switch
                              checked={storeData.charge_restocking_fee}
                              onCheckedChange={(checked) =>
                                handleStoreChange("charge_restocking_fee", checked)
                              }
                            />
                          </div>
                          {storeData.charge_restocking_fee && (
                            <div className="space-y-2">
                              <Label>Restocking Fee (%)</Label>
                              <Input
                                type="number"
                                name="restocking_fee_percentage"
                                value={storeData.restocking_fee_percentage}
                                onChange={(e) =>
                                  handleStoreChange("restocking_fee_percentage", e.target.value)
                                }
                                min="0"
                                max="100"
                              />
                            </div>
                          )}
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <Label>Charge Deposit</Label>
                            <Switch
                              checked={storeData.charge_deposit}
                              onCheckedChange={(checked) =>
                                handleStoreChange("charge_deposit", checked)
                              }
                            />
                          </div>
                          {storeData.charge_deposit && (
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Amount</Label>
                                <Input
                                  type="number"
                                  name="deposit_amount"
                                  value={storeData.deposit_amount}
                                  onChange={(e) =>
                                    handleStoreChange("deposit_amount", e.target.value)
                                  }
                                  min="0"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Deposit Tax Class</Label>
                                <ReactSelect
                                  options={[
                                    { value: "GST", label: "GST" },
                                    { value: "VAT", label: "VAT" },
                                    { value: "NA", label: "None" },
                                    { value: "Other", label: "Other" },
                                  ]}
                                  value={
                                    [
                                      { value: "GST", label: "GST" },
                                      { value: "VAT", label: "VAT" },
                                      { value: "NA", label: "None" },
                                      { value: "Other", label: "Other" },
                                    ].find(
                                      (opt) => opt.value === storeData.deposit_tax_class,
                                    ) || null
                                  }
                                  onChange={(opt) =>
                                    handleStoreChange("deposit_tax_class", opt?.value || "")
                                  }
                                  placeholder="Select tax class"
                                />
                              </div>
                              <div className="col-span-2 flex items-center justify-between">
                                <Label>Tax on Deposit</Label>
                                <Switch
                                  checked={storeData.charge_tax_on_deposit}
                                  onCheckedChange={(checked) =>
                                    handleStoreChange("charge_tax_on_deposit", checked)
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Security & Screen - hidden, not functional yet */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ display: 'none' }}>
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-xl flex items-center gap-2">
                          Security Settings
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                          <div className="space-y-0.5">
                            <Label>Two-Factor Auth</Label>
                            <p className="text-xs text-muted-foreground">
                              Mandatory 2FA for users
                            </p>
                          </div>
                          <Switch
                            checked={storeData.require_two_factor_auth}
                            onCheckedChange={(checked) =>
                              handleStoreChange("require_two_factor_auth", checked)
                            }
                          />
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-xl flex items-center gap-2">
                          Screen Settings
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label>Lock Screen</Label>
                            <p className="text-xs text-muted-foreground">
                              Auto-lock on inactivity
                            </p>
                          </div>
                          <Switch
                            checked={storeData.lock_screen_enabled}
                            onCheckedChange={(checked) =>
                              handleStoreChange("lock_screen_enabled", checked)
                            }
                          />
                        </div>
                        {storeData.lock_screen_enabled && (
                          <div className="space-y-2">
                            <Label>Timeout (Minutes)</Label>
                            <Input
                              type="number"
                              name="screen_timeout_minutes"
                              value={storeData.screen_timeout_minutes}
                              onChange={(e) =>
                                handleStoreChange("screen_timeout_minutes", e.target.value)
                              }
                              min="1"
                            />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {onboardingErrors.store && (
                    <div className="bg-destructive/10 text-destructive p-4 rounded-lg flex gap-3 items-center border border-destructive/20 animate-in shake duration-500">
                      <AlertCircle className="h-5 w-5 shrink-0" />
                      <p className="font-semibold">{onboardingErrors.store}</p>
                    </div>
                  )}

                  <div className="pt-6">
                    <Button
                      type="submit"
                      className="w-full h-12 text-lg font-bold shadow-lg shadow-primary/20 rounded-lg group"
                      disabled={storeMutation.isPending}
                    >
                      {storeMutation.isPending ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <span className="flex items-center gap-2">
                          Complete Store Setup
                          <Plus className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                        </span>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </TabsContent>

            <TabsContent value="4">
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <header className="space-y-1.5 pt-4">
                  <h2 className="text-3xl font-bold tracking-tight">
                    Add Your Products
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Get your inventory ready. You can add items manually or
                    import your existing catalog.
                  </p>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button
                    variant="outline"
                    className="group h-48 flex flex-col gap-4 border-dashed border-2 hover:border-primary hover:bg-primary/5 transition-all text-center p-6 rounded-xl"
                    onClick={() => navigate(all_routes.addproduct)}
                  >
                    <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                      <Plus className="h-7 w-7" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-bold text-lg">Manual Entry</p>
                      <p className="text-xs text-muted-foreground">
                        Add products one by one with full details
                      </p>
                    </div>
                  </Button>
                  <Button
                    variant="outline"
                    className="group h-48 flex flex-col gap-4 border-dashed border-2 hover:border-primary hover:bg-primary/5 transition-all text-center p-6 rounded-xl"
                    onClick={() => setShowProductImportModal(true)}
                  >
                    <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                      <UploadCloud className="h-7 w-7" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-bold text-lg">Bulk Import</p>
                      <p className="text-xs text-muted-foreground">
                        Upload Excel/CSV files to add many items at once
                      </p>
                    </div>
                  </Button>
                </div>

                <div className="flex flex-col md:flex-row gap-3 pt-4">
                  <Button
                    className="flex-1 h-12 text-lg font-bold rounded-lg shadow-lg shadow-primary/10"
                    onClick={() => setActiveStep(5)}
                  >
                    Continue to Next Step
                  </Button>
                  <Button
                    variant="ghost"
                    className="flex-1 h-12 text-lg text-muted-foreground rounded-lg"
                    onClick={() => setActiveStep(5)}
                  >
                    Skip for now
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="5">
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <header className="space-y-1.5 pt-4">
                  <h2 className="text-3xl font-bold tracking-tight">
                    Add Your Customers
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Connect with your clients to track sales history and build
                    loyalty.
                  </p>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button
                    variant="outline"
                    className="group h-48 flex flex-col gap-4 border-dashed border-2 hover:border-primary hover:bg-primary/5 transition-all text-center p-6 rounded-xl"
                    onClick={() => navigate(all_routes.customers)}
                  >
                    <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                      <Users className="h-7 w-7" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-bold text-lg">Add Manually</p>
                      <p className="text-xs text-muted-foreground">
                        Create new customer profiles individually
                      </p>
                    </div>
                  </Button>
                  <Button
                    variant="outline"
                    className="group h-48 flex flex-col gap-4 border-dashed border-2 hover:border-primary hover:bg-primary/5 transition-all text-center p-6 rounded-xl"
                    onClick={() => setShowCustomerImportModal(true)}
                  >
                    <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                      <UploadCloud className="h-7 w-7" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-bold text-lg">Bulk Import</p>
                      <p className="text-xs text-muted-foreground">
                        Upload Excel/CSV files to add many customers at once
                      </p>
                    </div>
                  </Button>
                </div>

                <div className="flex flex-col md:flex-row gap-3 pt-4">
                  <Button
                    className="flex-1 h-12 text-lg font-bold shadow-lg shadow-primary/20 rounded-lg"
                    onClick={() => setActiveStep(6)}
                  >
                    Finish Setup
                  </Button>
                  <Button
                    variant="ghost"
                    className="flex-1 h-12 text-lg text-muted-foreground rounded-lg"
                    onClick={() => setActiveStep(6)}
                  >
                    Skip for now
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="6">
              <div className="flex flex-col items-center justify-center text-center space-y-8 py-12 animate-in zoom-in duration-500">
                <div className="relative">
                  <div className="h-32 w-32 rounded-full bg-primary/10 flex items-center justify-center text-primary animate-bounce">
                    <Trophy className="h-16 w-16" />
                  </div>
                  <PartyPopper className="absolute -top-4 -right-4 h-10 w-10 text-amber-500 animate-pulse" />
                </div>

                <div className="space-y-3 max-w-md">
                  <h2 className="text-4xl font-black tracking-tight">
                    You're All Set!
                  </h2>
                  <p className="text-xl text-muted-foreground leading-relaxed">
                    Great job! Your store is fully configured and ready for
                    business. You've completed all the essential setup steps.
                  </p>
                </div>

                <div className="bg-muted px-6 py-4 rounded-2xl border flex items-center gap-4 text-left w-full max-w-md shadow-sm">
                  <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center text-white shrink-0">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-bold text-lg">Setup Complete</p>
                    <p className="text-sm text-muted-foreground">
                      Access all features from your dashboard.
                    </p>
                  </div>
                </div>

                <Button
                  size="lg"
                  className="w-full max-w-sm h-14 text-xl font-bold rounded-2xl shadow-xl shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                  onClick={onFinish}
                >
                  Go to Dashboard
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <MediaLibraryComponent
        isOpen={isMediaOpen}
        onClose={() => setIsMediaOpen(false)}
        onSelect={handleMediaSelect}
        isMultiple={false}
        selectedIds={selectedLogoId ? [selectedLogoId] : []}
      />

      {/* Product Bulk Import Modal */}
      <Dialog
        open={showProductImportModal}
        onOpenChange={(open) => {
          setShowProductImportModal(open);
          if (!open) setProductImportFile(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Products</DialogTitle>
            <DialogDescription>
              Upload a bulk product file to add multiple products at once.
              Supported formats: .xlsx, .csv.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={handleDownloadProductTemplate}>
                <FileDown className="mr-2 h-4 w-4" /> Download Template
              </Button>
            </div>
            <div className="grid gap-2">
              <Label>Upload File (XLSX, CSV)</Label>
              <Input
                type="file"
                accept=".xlsx,.csv"
                onChange={(e) => setProductImportFile(e.target.files[0])}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowProductImportModal(false);
                setProductImportFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => importProductMutation.mutate(productImportFile)}
              disabled={!productImportFile || importProductMutation.isPending}
            >
              {importProductMutation.isPending ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Bulk Import Modal */}
      <Dialog
        open={showCustomerImportModal}
        onOpenChange={(open) => {
          setShowCustomerImportModal(open);
          if (!open) setCustomerImportFile(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Customers</DialogTitle>
            <DialogDescription>
              Upload a bulk customer file to add multiple customers at once.
              Supported formats: .xlsx, .csv.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={handleDownloadCustomerTemplate}
              >
                <FileDown className="mr-2 h-4 w-4" /> Download Template
              </Button>
            </div>
            <div className="grid gap-2">
              <Label>Upload File (XLSX, CSV)</Label>
              <Input
                type="file"
                accept=".xlsx,.csv"
                onChange={(e) => setCustomerImportFile(e.target.files[0])}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCustomerImportModal(false);
                setCustomerImportFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => importCustomerMutation.mutate(customerImportFile)}
              disabled={!customerImportFile || importCustomerMutation.isPending}
            >
              {importCustomerMutation.isPending ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!importErrors}
        onOpenChange={(open) => !open && setImportErrors(null)}
      >
        <DialogContent
          className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Import Completed with Errors
            </DialogTitle>
            <DialogDescription>
              {importErrors?.message || "Some rows failed to import."} (Failed
              rows: {importErrors?.data?.failed})
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 py-4">
            <div className="rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="h-10 px-4 text-left font-medium">Row</th>
                    <th className="h-10 px-4 text-left font-medium">
                      Attribute
                    </th>
                    <th className="h-10 px-4 text-left font-medium">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {importErrors?.data?.failures?.map((f, i) => (
                    <tr
                      key={i}
                      className="border-t hover:bg-muted/50 transition-colors"
                    >
                      <td className="p-4 align-top font-medium">{f.row}</td>
                      <td className="p-4 align-top">{f.attribute || "-"}</td>
                      <td className="p-4 align-top text-destructive">
                        <ul className="list-disc pl-4 space-y-1">
                          {f.errors?.map((err, j) => (
                            <li key={j}>{err}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex justify-end border-t pt-4">
            <Button variant="outline" onClick={() => setImportErrors(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StepItem({
  number,
  title,
  isActive,
  isCompleted,
  disabled,
  isOptional,
  onClick,
}) {
  return (
    <div
      onClick={!disabled ? onClick : undefined}
      className={cn(
        "flex items-center gap-3 py-2.5 px-4 rounded-xl transition-all duration-300",
        isActive &&
        "bg-primary text-primary-foreground shadow-lg shadow-primary/20",
        !isActive && !disabled && "hover:bg-muted cursor-pointer",
        disabled && !isActive && "opacity-70 cursor-not-allowed",
        disabled && isActive && "cursor-not-allowed",
      )}
    >
      <div
        className={cn(
          "h-8 w-8 rounded-lg border-2 flex items-center justify-center text-xs font-bold shrink-0 transition-all",
          isCompleted
            ? "bg-green-500 border-green-500 text-white shadow-md shadow-green-500/10"
            : isActive
              ? "border-primary-foreground bg-primary-foreground text-primary shadow-sm"
              : "border-muted-foreground/30 text-muted-foreground",
        )}
      >
        {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : number}
      </div>
      <div className="flex flex-col">
        <span
          className={cn(
            "text-sm font-bold leading-none",
            isActive ? "text-primary-foreground" : "text-muted-foreground",
          )}
        >
          {title}
        </span>
        {isOptional && (
          <span
            className={cn(
              "text-[10px] uppercase tracking-wider font-bold mt-1 px-1.5 py-0.5 rounded w-fit",
              isActive
                ? "bg-primary-foreground/20 text-primary-foreground/80"
                : "bg-muted text-muted-foreground",
            )}
          >
            Optional
          </span>
        )}
      </div>
    </div>
  );
}

function PasswordField({ id, label, value, onChange, show, toggleShow, hint }) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        {hint && (
          <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-tight">
            {hint}
          </span>
        )}
      </div>
      <div className="relative group">
        <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
        <Input
          id={id}
          type={show ? "text" : "password"}
          className="pl-10 h-12 transition-all focus:ring-2 focus:ring-primary/20"
          value={value}
          onChange={onChange}
        />
        <button
          type="button"
          onClick={toggleShow}
          className="absolute right-3 top-3.5 text-muted-foreground hover:text-foreground transition-colors"
        >
          {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}
